import { KeyOutlined } from '@ant-design/icons';
import {
  Button,
  Col,
  Form,
  type FormProps,
  Input,
  Modal,
  Row,
  Space,
  theme,
} from 'antd';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ExpirationDatePicker } from '@client/components/date/ExpirationDatePicker';
import { NotificationContext } from '@client/context/NotificationContext';
import { useApiKeys } from '@client/hooks/useApiKeys';
import { useFormProps } from '@client/hooks/useFormProps';
import { copyToClipboard, stopPropagation } from '@client/utils/document.util';
import { logger } from '@client/utils/logger.util';
import { DEFAULT_API_KEY, type IApiKey, type ITenant } from '@common/types';

import styles from '../tenants.module.less';

import type { AxiosResponse } from 'axios';

type TGenerateApiKeyProps = {
  tenant: ITenant | null;
  disabled: boolean;
};

export const GenerateApiKey = (props: TGenerateApiKeyProps) => {
  const { tenant, disabled } = props;

  const [formRef] = Form.useForm();
  const [isMainModalOpen, setIsMainModalOpen] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [apiKey, setApiKey] = useState<string>(null);

  const { onGenerateApiKey, loading } = useApiKeys();
  const { messageApi } = useContext(NotificationContext);

  const {
    token: { colorError },
  } = theme.useToken();

  const { t } = useTranslation();

  const showMainModal = () => {
    setIsMainModalOpen(true);
  };

  const showInfoModal = () => {
    setIsInfoModalOpen(true);
  };

  const handleCancel = () => {
    setIsMainModalOpen(false);
    setIsInfoModalOpen(false);
  };

  const handleCopyApiKey = async () => {
    await copyToClipboard(apiKey)
      .then(async () => messageApi.success(t('tenant.api.keyCopied')))
      .catch(async (error) => messageApi.error(t(error.message)));
  };

  const handleOk = async () => {
    const values = await formRef.validateFields();
    const expiresAtValue = values.metadata?.expiresAt;
    const expiresAt = expiresAtValue
      ? typeof expiresAtValue.toDate === 'function'
        ? expiresAtValue.toDate()
        : new Date(expiresAtValue)
      : null;

    if (!tenant) return;

    await onGenerateApiKey(tenant, expiresAt)
      .then(({ data }: AxiosResponse<{ apiKey: string; apiKeyId: string }>) => {
        setApiKey(data.apiKey);
        setIsMainModalOpen(false);
        showInfoModal();
      })
      .catch(() => {
        // Error is handled in useTenant hook
      });
  };

  const { DEFAULT_FORM_PROPS } = useFormProps(loading, formRef);

  const formProps = {
    ...DEFAULT_FORM_PROPS,
    onFieldsChange: () => {
      // TODO (teamco): Do nothing
      // Override to prevent setting dirty on form change, as we don't want to track changes in this form
    },
    async onFinish(formValues: Partial<IApiKey>) {
      logger.info('formValues', formValues);
    },
    initialValues: { DEFAULT_API_KEY, name: tenant?.name },
  } as FormProps<Partial<IApiKey>>;

  return (
    <>
      <Button
        icon={<KeyOutlined />}
        disabled={disabled || !tenant}
        loading={loading}
        onClick={(e) => {
          stopPropagation(e);
          showMainModal();
        }}
      >
        {t('tenant.api.handleKey')}
      </Button>

      <Modal
        title={
          <div className={styles.chipLabel}>
            <KeyOutlined />
            {t('tenant.api.generateKey')}
          </div>
        }
        width={640}
        mask={{ closable: false }}
        closable={false}
        open={isMainModalOpen}
        onCancel={handleCancel}
        rootClassName={styles.apiKeyModal}
        modalRender={(modal) => <div onClick={stopPropagation}>{modal}</div>}
        footer={[
          <Button key="cancel" loading={loading} onClick={handleCancel}>
            {t('actions.cancel')}
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={loading}
            onClick={handleOk}
          >
            {t('actions.generate')}
          </Button>,
        ]}
      >
        <p>{t('tenant.api.token.info', { name: tenant?.name })}</p>
        <Form {...formProps}>
          <Row gutter={[48, 24]}>
            <Col span={24}>
              <Form.Item
                label="Name"
                name="name"
                help={t('tenant.api.name.helper')}
              >
                <Input disabled={true} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                help={t('tenant.api.token.helper')}
                name={['metadata', 'expiresAt']}
                label={t('tenant.api.expiration')}
              >
                <ExpirationDatePicker
                  fieldName={['metadata', 'expiresAt']}
                  label={t('tenant.api.expiration')}
                  formRef={formRef}
                  disabled={disabled}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      <Modal
        title={
          <div className={styles.chipLabel}>
            <KeyOutlined />
            {t('tenant.api.keyCreated')}
          </div>
        }
        width={640}
        mask={{ closable: false }}
        closable={false}
        open={isInfoModalOpen}
        onCancel={handleCancel}
        rootClassName={styles.apiKeyModal}
        footer={[
          <Button key="cancel" loading={loading} onClick={handleCancel}>
            {t('actions.cancel')}
          </Button>,
        ]}
      >
        <p style={{ marginTop: 32 }}>
          <strong>{t('tenant.api.keys.info.attention')}</strong>{' '}
          {t('tenant.api.keys.info.attention.helper')}
        </p>
        <Space.Compact style={{ width: '100%' }} size="large">
          <Input value={apiKey} readOnly />
          <Button type="primary" onClick={handleCopyApiKey}>
            {t('actions.copy')}
          </Button>
        </Space.Compact>
        <p style={{ marginTop: 16, color: colorError }}>
          {t('tenant.api.keys.info.attention.alert')}
        </p>
      </Modal>
    </>
  );
};
